import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, Recycle, Coins, ShoppingBag, Leaf } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1614091066096-4c6521fbea3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const cats = [
  { key: "rice", label: "Rice", img: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=600&q=80" },
  { key: "grains", label: "Grains", img: "https://images.unsplash.com/photo-1568254183919-78a4f43a2877?w=600&q=80" },
  { key: "pulses", label: "Pulses", img: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=600&q=80" },
];

const steps = [
  { icon: Recycle, title: "Trade", text: "Give us eligible household rice." },
  { icon: Coins, title: "Earn", text: "1 kg = ₹10 RicePoints." },
  { icon: ShoppingBag, title: "Shop", text: "Redeem on rice, grains & pulses." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => { api.get("/products").then(({ data }) => setFeatured(data.slice(0, 8))); }, []);

  return (
    <div>
      {/* HERO — ecommerce first */}
      <section className="relative overflow-hidden grain-texture">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center py-14 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground px-3 py-1.5 rounded-full">
              <Leaf size={14} /> Trade. Earn. Shop. Grow.
            </span>
            <h1 className="mt-5 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Rice, Grains & Pulses,<br /><span className="text-primary">Delivered Fresh</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-lg">
              Shop quality, manufacturer-sealed staples for your home. Plus, trade your old rice and earn RicePoints on every purchase.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" asChild data-testid="hero-shop-btn">
                <Link to="/shop/rice">SHOP NOW <ArrowRight size={18} /></Link>
              </Button>
              <Button size="lg" variant="outline" asChild data-testid="hero-trade-btn">
                <Link to="/trade">TRADE YOUR RICE</Link>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><Truck size={16} className="text-primary" /> Doorstep delivery</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> Sealed & quality-checked</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <img src={HERO} alt="Rice harvest" className="rounded-2xl border border-border shadow-[0_20px_60px_rgba(0,0,0,0.12)] w-full max-h-[520px] object-cover" />
            <div className="absolute -bottom-5 -left-3 sm:left-6 bg-card border border-border rounded-xl shadow-lg px-5 py-4">
              <div className="text-xs text-muted-foreground">Trade old rice, earn</div>
              <div className="font-heading font-bold text-lg">₹10 RicePoints / kg</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Shop by category</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {cats.map((c) => (
            <Link key={c.key} to={`/shop/${c.key}`} data-testid={`home-cat-${c.key}`}
                  className="group relative rounded-xl overflow-hidden border border-border aspect-[16/9] sm:aspect-[4/3] rb-lift">
              <img src={c.img} alt={c.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white font-heading font-bold text-2xl flex items-center gap-2">
                {c.label} <ArrowRight size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Popular this week</h2>
          <Link to="/shop" className="text-sm font-semibold text-primary flex items-center gap-1" data-testid="home-view-all">View all <ArrowRight size={15} /></Link>
        </div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="home-featured">
          {featured.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* RICEPOINTS HIGHLIGHT (secondary) */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <div className="bg-secondary text-secondary-foreground rounded-2xl p-6 sm:p-10 grain-texture">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-secondary-foreground/60 flex items-center gap-2"><Coins size={14} /> RicePoints Rewards</div>
              <h2 className="mt-3 font-heading font-extrabold text-3xl">Turn old rice into savings</h2>
              <p className="mt-2 text-secondary-foreground/80 max-w-xl">Trade eligible household rice and earn RicePoints — then use up to 15% of your next eligible order value on rice, grains & pulses.</p>
              <Button className="mt-5" variant="default" asChild data-testid="rp-cta"><Link to="/trade">Start a trade-in <ArrowRight size={16} /></Link></Button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {steps.map((s) => (
                <div key={s.title} className="bg-white/10 rounded-xl p-4">
                  <s.icon size={22} className="text-primary" />
                  <div className="mt-2 font-heading font-bold">{s.title}</div>
                  <div className="text-xs text-secondary-foreground/70">{s.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
